'use client'

import { useEffect, useRef, useState } from 'react'
import { PublicConfigSchema } from '@mist/contracts/api'
import { SHOPIFY_ATTRIBUTION_KEYS, ShopifyCheckoutAttributeSchema, type ShopifyCheckoutAttribute } from '@mist/contracts/shopify'
import { getApi } from '@/lib/api-client'
import { isCommerceAvailable } from '@/lib/public-config'
import { SHOPIFY_BUY_SDK_URL } from '@/lib/shopify-config'
import { captureAttribution } from '@/lib/attribution'
import { readSubscriberHandoff } from '@/lib/session'
import { trackEvent } from '@/lib/analytics'

/**
 * MIST-0407 lazy Shopify Buy Button SDK checkout (R-COMMERCE-02, R-ARCH-06, Section 8.4).
 *
 * The SDK is fetched ONLY when the authoritative public config permits checkout.
 * Checkout attribution uses the Storefront checkout customAttributes path, whose
 * attributes propagate to the order webhook as REST `note_attributes`; the click
 * handler awaits that update before navigating to Shopify.
 */
interface ShopifyCheckout {
  readonly id: string
  readonly webUrl: string
  readonly customAttributes?: readonly ShopifyCheckoutAttribute[]
}

interface ShopifyClient {
  readonly checkout: {
    create(input: { lineItems: Array<{ variantId: string; quantity: number }> }): Promise<ShopifyCheckout>
    updateAttributes(checkoutId: string, input: { customAttributes: ShopifyCheckoutAttribute[] }): Promise<ShopifyCheckout>
  }
  readonly product?: {
    fetch(productId: string): Promise<{ variants: Array<{ id: string }> }>
  }
}

interface ShopifyGlobal {
  buildClient: (config: { domain: string; storefrontAccessToken: string }) => ShopifyClient
}

declare global {
  interface Window {
    ShopifyBuy?: ShopifyGlobal
    __mistShopify?: {
      attributes: Record<string, string>
      customAttributes: readonly ShopifyCheckoutAttribute[]
      sdkLoaded: boolean
      checkoutId?: string
    }
  }
}

const SDK_URL = SHOPIFY_BUY_SDK_URL
const DOMAIN = process.env.NEXT_PUBLIC_SHOPIFY_DOMAIN ?? ''
const TOKEN = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN ?? ''
const PRODUCT_ID = process.env.NEXT_PUBLIC_SHOPIFY_PRODUCT_ID ?? ''
const VARIANT_ID = process.env.NEXT_PUBLIC_SHOPIFY_VARIANT_ID ?? ''
const ATTRIBUTION_KEYS: Record<string, true> = {
  [SHOPIFY_ATTRIBUTION_KEYS.subscriberPseudonymousId]: true,
  [SHOPIFY_ATTRIBUTION_KEYS.campaignPseudonymousId]: true,
}

type Phase = 'checking' | 'unavailable' | 'ready'
type CheckoutPhase = 'idle' | 'loading' | 'error'

export default function ShopifyBuy() {
  const [phase, setPhase] = useState<Phase>('checking')
  const [sdkReady, setSdkReady] = useState(false)
  const [checkoutPhase, setCheckoutPhase] = useState<CheckoutPhase>('idle')
  const checked = useRef(false)
  const mounted = useRef(false)
  const clientRef = useRef<ShopifyClient | null>(null)

  // Re-check the authoritative public config before loading anything: before
  // launch this resolves to `unavailable` so no SDK, price, or checkout appears.
  useEffect(() => {
    if (checked.current) return
    checked.current = true
    void getApi('/api/v1/public/config', PublicConfigSchema)
      .then((config) => setPhase(isCommerceAvailable(config) ? 'ready' : 'unavailable'))
      .catch(() => setPhase('unavailable'))
  }, [])

  useEffect(() => {
    if (phase !== 'ready' || mounted.current) return
    mounted.current = true
    loadSdk()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  function pseudonymousAttributes(): Record<string, string> {
    const attrs: Record<string, string> = {}
    const handoff = readSubscriberHandoff()
    if (handoff) attrs[SHOPIFY_ATTRIBUTION_KEYS.subscriberPseudonymousId] = handoff.subscriberPublicId
    const campaign = captureAttribution().referralCampaignId
    if (campaign) attrs[SHOPIFY_ATTRIBUTION_KEYS.campaignPseudonymousId] = campaign
    return attrs
  }

  function checkoutAttributes(attrs: Record<string, string>): ShopifyCheckoutAttribute[] {
    return ShopifyCheckoutAttributeSchema.array().parse(
      Object.entries(attrs).map(([key, value]) => ({ key, value })),
    )
  }

  function mergeCustomAttributes(checkout: ShopifyCheckout, metadata: ShopifyCheckoutAttribute[]): ShopifyCheckoutAttribute[] {
    const existing = checkout.customAttributes ?? []
    return [
      ...existing.filter((attribute) => ATTRIBUTION_KEYS[attribute.key] !== true),
      ...metadata,
    ]
  }
  function assertMetadataReturned(checkout: ShopifyCheckout, metadata: ShopifyCheckoutAttribute[]) {
    const returned = checkout.customAttributes ?? []
    for (const attribute of metadata) {
      if (!returned.some((candidate) => candidate.key === attribute.key && candidate.value === attribute.value)) {
        throw new Error('Shopify checkout did not return attribution customAttributes')
      }
    }
  }

  async function variantId(client: ShopifyClient): Promise<string> {
    if (VARIANT_ID) return VARIANT_ID
    if (!client.product) throw new Error('Shopify product client is not available')
    const product = await client.product.fetch(PRODUCT_ID)
    const variant = product.variants[0]
    if (!variant) throw new Error('Shopify product has no variants')
    return variant.id
  }

  function initClient() {
    const sdk = window.ShopifyBuy
    if (!sdk) return
    clientRef.current = sdk.buildClient({ domain: DOMAIN, storefrontAccessToken: TOKEN })
    setSdkReady(true)
    window.__mistShopify = { attributes: {}, customAttributes: [], sdkLoaded: true }
  }

  function loadSdk() {
    if (window.ShopifyBuy) {
      initClient()
      return
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-mist-shopify]')
    if (existing) {
      existing.addEventListener('load', initClient, { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = SDK_URL
    script.async = true
    script.dataset['mistShopify'] = 'true'
    script.addEventListener('load', initClient, { once: true })
    document.head.appendChild(script)
  }

  async function startCheckout() {
    const client = clientRef.current
    if (!client) return
    setCheckoutPhase('loading')
    const attributes = pseudonymousAttributes()
    const metadata = checkoutAttributes(attributes)
    trackEvent('mist_shopify_buy_clicked', { checkout_status: 'buy_clicked' })
    try {
      const checkout = await client.checkout.create({ lineItems: [{ variantId: await variantId(client), quantity: 1 }] })
      const attributedCheckout = metadata.length > 0
        ? await client.checkout.updateAttributes(checkout.id, { customAttributes: mergeCustomAttributes(checkout, metadata) })
        : checkout
      assertMetadataReturned(attributedCheckout, metadata)
      window.__mistShopify = {
        attributes,
        customAttributes: attributedCheckout.customAttributes ?? [],
        sdkLoaded: true,
        checkoutId: attributedCheckout.id,
      }
      trackEvent('mist_checkout_started', { checkout_status: 'started', unattributed: metadata.length === 0 })
      window.location.assign(attributedCheckout.webUrl)
    } catch {
      setCheckoutPhase('error')
    }
  }

  if (phase !== 'ready') return null

  return (
    <section aria-labelledby="buy" id="commerce" style={{ marginTop: 'var(--mist-space-12)' }}>
      <h2 id="buy">Buy Edition 001</h2>
      <p className="hint" style={{ marginTop: 'var(--mist-space-3)' }}>
        Sold and fulfilled by PRNTD. Secure checkout by Shopify.
      </p>
      <button className="mist-button mist-button--primary" type="button" onClick={startCheckout} disabled={!sdkReady || checkoutPhase === 'loading'}>
        {checkoutPhase === 'loading' ? 'Opening Shopify…' : 'Buy Edition 001'}
      </button>
      {checkoutPhase === 'error' ? (
        <p className="error" role="alert" style={{ marginTop: 'var(--mist-space-3)' }}>
          Shopify checkout could not open. Please try again.
        </p>
      ) : null}
    </section>
  )
}
